"use client";

import React from "react";
import { motion, useReducedMotion } from "framer-motion";
import { Compass, Sparkles, SlidersHorizontal as Sliders, Shield, Zap, Wrench, PackageCheck, Truck } from 'lucide-react';
interface CriterionItem {
  id: string;
  code: string;
  title: string;
  description: string;
  icon: React.ElementType;
}
const CRITERIA: CriterionItem[] = [{
  id: "intended-use",
  code: "CRIT-01",
  title: "Intended Use",
  description: "Where and how will the machine operate?",
  icon: Compass
}, {
  id: "new-or-used",
  code: "CRIT-02",
  title: "New or Used",
  description: "Which starting condition fits your budget and priorities?",
  icon: Sparkles
}, {
  id: "controller",
  code: "CRIT-03",
  title: "Controller",
  description: "What controls and operating information are available?",
  icon: Sliders
}, {
  id: "safety-arena",
  code: "CRIT-04",
  title: "Safety Arena",
  description: "Is the inflatable included, and what does the product require?",
  icon: Shield
}, {
  id: "power-space",
  code: "CRIT-05",
  title: "Power & Space",
  description: "Does the exact system fit the location?",
  icon: Zap
}, {
  id: "parts-availability",
  code: "CRIT-06",
  title: "Parts Availability",
  description: "Can important replacement components still be sourced?",
  icon: Wrench
}, {
  id: "price-package",
  code: "CRIT-07",
  title: "Price & Package",
  description: "What exactly does the advertised price include?",
  icon: PackageCheck
}, {
  id: "shipping",
  code: "CRIT-08",
  title: "Shipping",
  description: "How will the equipment reach the destination?",
  icon: Truck
}];
export default function BuyerCriteria() {
  const shouldReduceMotion = useReducedMotion();
  return <section id="buyerguidecriteria" data-nav="light" className="bg-background text-foreground border-b border-border py-20 md:py-28">
      <div className="mx-auto max-w-[80rem] px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <div className="max-w-3xl mb-12 md:mb-16">
          <div className="inline-flex items-center gap-2 border border-border bg-muted/60 px-2.5 py-1 mb-4 text-xs font-mono font-medium tracking-wider uppercase text-muted-foreground">
            <span className="inline-block size-1.5 bg-primary" />
            EQUIPMENT SPECIFICATION CRITERIA
          </div>
          <h2 className="font-serif text-3xl sm:text-4xl md:text-5xl font-bold tracking-tight uppercase text-foreground leading-[1.05]">
            What Should You Look for When Buying a Mechanical Bull?
          </h2>
          <p className="mt-5 text-base sm:text-lg text-muted-foreground leading-relaxed">
            Start with the intended use, then compare the exact equipment package.
            The most important questions are whether the machine fits your
            operating environment, what comes with it, how the operator controls
            it, how much space and power it requires, whether replacement parts
            are available and what support or warranty applies to that specific
            product.
          </p>
        </div>

        {/* 8 Criteria Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-5">
          {CRITERIA.map((item, i) => {
          const Icon = item.icon;
          return <motion.div key={item.id} data-index={i} initial={shouldReduceMotion ? {
            opacity: 1,
            y: 0
          } : {
            opacity: 0,
            y: 12
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true,
            amount: 0.2
          }} transition={{
            duration: 0.45,
            delay: shouldReduceMotion ? 0 : i * 0.04,
            ease: [0.16, 1, 0.3, 1]
          }} className="group relative flex flex-col justify-between rounded-none border border-border bg-card p-6 text-card-foreground transition-[border-color,background-color] duration-200 hover:border-foreground">
                <div>
                  <div className="flex items-center justify-between pb-4 mb-4 border-b border-border">
                    <span className="font-mono text-xs font-semibold text-muted-foreground group-hover:text-foreground transition-colors duration-200">
                      {item.code}
                    </span>
                    <Icon className="size-4 text-muted-foreground group-hover:text-primary transition-colors duration-200" />
                  </div>

                  <h3 className="font-serif text-xl sm:text-2xl font-bold uppercase tracking-tight text-foreground">
                    {item.title}
                  </h3>
                  <p className="mt-3 text-sm text-muted-foreground leading-snug">
                    {item.description}
                  </p>
                </div>

                <div className="mt-8 pt-3 border-t border-border/50 flex items-center justify-between font-mono text-[11px] uppercase tracking-wider text-muted-foreground/80">
                  <span>CHECK ITEM</span>
                  <span className="text-primary font-bold">0{i + 1}</span>
                </div>
              </motion.div>;
        })}
        </div>
      </div>
    </section>;
}